import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    content: "",
    rating: 5
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?limit=20`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Failed to fetch testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (user?.avatar) {
        payload.image = user.avatar;
      }
      await axios.post(`${API}/testimonials`, payload);
      toast.success("Thank you! Your review has been submitted for approval.");
      setShowForm(false);
      setFormData({ name: "", role: "", content: "", rating: 5 });
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openForm = () => {
    if (user) {
      setFormData(prev => ({ ...prev, name: prev.name || user.name || "" }));
    }
    setShowForm(true);
  };

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="testimonials-page">
      <SEO title="Testimonials" description="Read what our customers say about Praroha Greens microgreens. Real reviews from real people." keywords="praroha greens reviews, customer testimonials, microgreens feedback" path="/testimonials" />
      {/* Compact Hero with Stats */}
      <section className="py-10 md:py-14 bg-praroha-forest" data-testid="testimonials-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Title & CTA */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <span className="inline-block px-4 py-1.5 bg-white/10 text-white/90 text-sm font-medium rounded-full mb-4">
                Reviews
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
                What Our Customers Say
              </h1>
              <p className="text-white/60 mb-6 max-w-md">
                Real stories from real customers about their experience with Praroha Greens microgreens.
              </p>
              <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full px-6 py-5"
                    data-testid="write-review-btn"
                    onClick={openForm}
                  >
                    <Star className="mr-2 w-4 h-4" />
                    Write a Review
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" data-testid="review-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-praroha-forest">
                      Share Your Experience
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {user?.avatar && (
                      <div className="flex items-center gap-3 p-3 bg-praroha-cream/40 rounded-xl" data-testid="review-avatar-preview">
                        <img src={user.avatar.startsWith("/api") ? `${API.replace('/api','')}${user.avatar}` : user.avatar} alt="Your photo" className="w-10 h-10 rounded-full object-cover" />
                        <p className="text-sm text-muted-foreground">Your profile photo will appear with your review</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter your name"
                        required
                        data-testid="review-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Your Role</Label>
                      <Input
                        id="role"
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        placeholder="e.g., Home Chef, Fitness Enthusiast"
                        data-testid="review-role-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rating</Label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFormData({ ...formData, rating: star })}
                            className="p-1"
                            data-testid={`rating-star-${star}`}
                          >
                            <Star
                              className={`w-6 h-6 ${
                                star <= formData.rating
                                  ? "fill-praroha-terracotta text-praroha-terracotta"
                                  : "text-muted-foreground"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="content">Your Review</Label>
                      <Textarea
                        id="content"
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        placeholder="Share your experience with Praroha Greens..."
                        rows={4}
                        required
                        data-testid="review-content-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full"
                      data-testid="submit-review-btn"
                    >
                      {submitting ? "Submitting..." : "Submit Review"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </motion.div>

            {/* Right - Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { value: "2000+", label: "Happy Customers" },
                { value: "4.9", label: "Average Rating" },
                { value: "98%", label: "Would Recommend" },
                { value: "500+", label: "5-Star Reviews" }
              ].map((stat) => (
                <div key={stat.label} className="bg-white/10 border border-white/10 rounded-2xl p-5 text-center">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-praroha-terracotta">{stat.value}</p>
                  <p className="text-white/60 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 bg-background" data-testid="testimonials-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-muted animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No testimonials yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.08, 0.5) }}
                  className="break-inside-avoid"
                >
                  <Card className="group bg-white border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                    <CardContent className="p-6">
                      {/* Header */}
                      <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border/50">
                        <div className="w-12 h-12 shrink-0 rounded-full bg-praroha-forest/10 flex items-center justify-center overflow-hidden group-hover:bg-praroha-forest transition-colors duration-300">
                          {testimonial.image ? (
                            <img src={testimonial.image.startsWith("/api") ? `${API.replace('/api','')}${testimonial.image}` : testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-lg font-heading font-bold text-praroha-forest group-hover:text-white transition-colors">{testimonial.name.split(' ').slice(-1)[0].charAt(0)}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-heading text-base font-semibold text-praroha-forest leading-tight truncate">
                            {testimonial.name}
                          </h4>
                          <p className="text-xs text-praroha-sage mt-1 leading-snug">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                      {/* Content */}
                      <Quote className="w-6 h-6 text-praroha-terracotta/20 mb-3" />
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {testimonial.content}
                      </p>
                      <div className="flex gap-1 mt-4">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < testimonial.rating
                                ? "fill-praroha-terracotta text-praroha-terracotta"
                                : "text-muted-foreground"
                            }`}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

    </div>
  );
}
